import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import DarkModeToggle from "./DarkModeToggle";

/**
 * 공용 헤더 — 모든 라우트가 동일하게 렌더한다(타이틀/구조 고정). 페이지 전환 시
 * 헤더는 그대로 유지되고 body 만 바뀐다. `right` 슬롯에 페이지별 컨트롤
 * (예: 새로고침 인디케이터)을 넣고, 다크모드 토글은 항상 마지막에 둔다.
 *
 * 관리자 링크는 공용 탭(PageTabs)이 아니라 헤더 구석에 둔다 — 조회하러 온 사람의
 * 동선을 방해하지 않으면서 운영자는 어느 화면에서든 바로 들어갈 수 있다.
 * 접근 제어는 서버가 하므로 링크 노출 자체는 문제되지 않는다.
 */
export default function AppHeader({
  right,
  showAdminLink = true,
}: {
  right?: ReactNode;
  /** 관리자 화면 자신은 이 링크를 숨긴다(이미 그 화면이라 의미 없음). */
  showAdminLink?: boolean;
}) {
  return (
    <header className="border-b border-surface-border bg-surface dark:border-surface-darkBorder dark:bg-surface-darkSubtle">
      <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6 sm:py-6">
        {/*
          로고는 실시간 대시보드로 돌아가는 링크다 — 이력/카메라/관리자 어디에 있든
          좌상단을 누르면 홈으로 온다는 건 웹에서 학습된 기대라, 링크가 아니면 오히려
          "왜 안 눌리지" 가 된다.

          링크를 `inline-flex` 로 두는 이유: 모바일에서 h1 은 세로 스택의 flex 아이템이라
          가로로 늘어나는데, 그대로 두면 글자 오른쪽 빈 공간까지 클릭 영역이 되어
          엉뚱한 곳을 눌러도 페이지가 이동한다. inline-flex 면 글자 폭에만 붙는다.
        */}
        <h1 className="text-lg font-bold tracking-tight sm:text-2xl">
          <Link
            to="/"
            aria-label="vestellalab status — 실시간 대시보드로 이동"
            className="inline-flex min-h-[2.75rem] items-center rounded transition-colors hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 sm:min-h-0 dark:hover:text-slate-300"
          >
            vestellalab status
          </Link>
        </h1>
        {/*
          모바일에서는 제목 아래 한 줄을 통째로 쓰므로 양끝 정렬한다 — 갱신 문구는 왼쪽,
          버튼들은 오른쪽. 좌측 정렬로 몰아두면 버튼 세 개가 가운데 뭉쳐 눌러야 할 게
          어디까지인지 읽히지 않는다.
        */}
        <div className="flex items-center justify-between gap-2 sm:justify-end sm:gap-3">
          {right}
          {/* 버튼류는 한 덩어리로 묶어 오른쪽 끝에 붙인다 — 낱개로 흩뿌리면
              '관리자'가 갱신 문구와 토글 사이에 떠서 무엇의 일부인지 읽히지 않는다. */}
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            {showAdminLink && (
              <Link
                to="/admin"
                // 글자만 있으면 터치 영역이 31x16 밖에 안 된다 — 모바일에서 패딩으로 44px 확보.
                className="inline-flex min-h-[2.75rem] items-center px-2 text-xs text-slate-400 underline-offset-2 transition-colors hover:text-slate-600 hover:underline sm:min-h-0 sm:px-0 dark:text-slate-500 dark:hover:text-slate-300"
              >
                관리자
              </Link>
            )}
            <DarkModeToggle />
          </div>
        </div>
      </div>
    </header>
  );
}
